/**
 * Dashboard panel → plugin widget, rendered with the OWNING plugin's
 * context. Previously widgets got the dashboard's api client and props, so
 * widgets that call their own backend (goals, mood, …) broke on the
 * dashboard while working on their plugin's pages.
 *
 * With the embed contract (SDK ≥ 1.4) the widget renders through
 * PluginEmbedHost (scoped api, events, PluginUIProvider, crash isolation
 * that never disables the plugin). On older SDKs it falls back to the lazy
 * component with the same owner context provided directly.
 */

import React, { Suspense, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { PluginUIProvider } from '@neuro/plugin-ui';
import { useAuthStore } from '../../stores/authStore';
import { getEmbedSdk } from '../embed/sdk-embed';
import {
  buildPluginHostContext, getPluginEventsApi, getScopedPluginApi, resolvePluginCapabilities,
} from '../runtime/plugin-runtime-context';
import { PluginEmbedHost } from './PluginEmbedHost';

export interface DashboardPluginWidgetProps {
  pluginId: string;
  /** `${pluginId}:${widgetId}` — also the widget's embed alias. */
  panelId: string;
  /** Lazy widget component (fallback path). */
  Component: React.ComponentType<any>;
  /** Props from the dashboard ({ config, … }). */
  dashboardProps: Record<string, unknown>;
}

const loading = <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--nc-color-text-muted, #888)' }}>Loading widget…</div>;

/** Owner props win over anything the dashboard passes (its api client belongs to the dashboard). */
export function ownerWidgetProps(pluginId: string, userId: string, navigate: (path: string) => void, dashboardProps: Record<string, unknown>): Record<string, unknown> {
  const api = getScopedPluginApi(pluginId);
  return { ...dashboardProps, api, apiClient: api, pluginId, userId, navigate, events: getPluginEventsApi(pluginId) };
}

function FallbackWidget({ pluginId, Component, dashboardProps }: Omit<DashboardPluginWidgetProps, 'panelId'>) {
  const navigate = useNavigate();
  const userId = useAuthStore((s) => s.userId) ?? 'unknown';
  const props = useMemo(() => ownerWidgetProps(pluginId, userId, navigate, dashboardProps), [pluginId, userId, navigate, dashboardProps]);
  const hostContext = useMemo(() => buildPluginHostContext({
    pluginId, userId, navigate, api: props.api as never, events: props.events as never,
  }), [pluginId, userId, navigate, props.api, props.events]);
  return (
    <PluginUIProvider pluginId={pluginId} version="1.0.0" capabilities={resolvePluginCapabilities(pluginId, 'DashboardPluginWidget')} hostContext={hostContext}>
      <Suspense fallback={loading}>
        <Component {...props} />
      </Suspense>
    </PluginUIProvider>
  );
}

export function DashboardPluginWidget({ pluginId, panelId, Component, dashboardProps }: DashboardPluginWidgetProps) {
  const sdk = getEmbedSdk();
  const descriptor = sdk?.registry.get(panelId);
  if (sdk && descriptor && descriptor.pluginId === pluginId) {
    const config = dashboardProps.config && typeof dashboardProps.config === 'object' ? dashboardProps.config as Record<string, unknown> : {};
    return <PluginEmbedHost componentId={panelId} instanceId={`dashboard:${panelId}`} props={config} mode="run" loadingFallback={loading} />;
  }
  return <FallbackWidget pluginId={pluginId} Component={Component} dashboardProps={dashboardProps} />;
}
